import { useState, useEffect } from 'react'
import { usePermissions, Permission } from '../../hooks/usePermissions'
import { getApiUrl, createFetchConfig } from '../../config/api'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  ChartBarIcon,
  InformationCircleIcon,
  AdjustmentsHorizontalIcon,
  HashtagIcon,
  CalendarIcon,
  ListBulletIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline'

interface CustomField {
  id: string
  name: string
  key: string
  type: 'TEXT' | 'SELECT' | 'NUMBER' | 'DATE'
  options: string[] | null
  required: boolean
  isGlobal: boolean
  createdAt: string
  updatedAt: string
  creator: {
    id: string
    name: string
    email: string
  }
  _count: {
    projects: number
    values: number
  }
}

interface CreateFieldData {
  name: string
  key: string
  type: 'TEXT' | 'SELECT' | 'NUMBER' | 'DATE'
  options: string[]
  required: boolean
  isGlobal: boolean
}

const CustomFieldManagement = () => {
  const { hasPermission, isSuperAdmin } = usePermissions()

  const [customFields, setCustomFields] = useState<CustomField[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingField, setEditingField] = useState<string | null>(null)
  const [editingType, setEditingType] = useState<'name' | 'options' | 'required' | null>(null)
  const [editingValue, setEditingValue] = useState<any>('')

  const [newField, setNewField] = useState<CreateFieldData>({
    name: '',
    key: '',
    type: 'TEXT',
    options: [''],
    required: false,
    isGlobal: true
  })

  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  // 權限檢查
  const canManageFields = hasPermission(Permission.MANAGE_CUSTOM_FIELDS)
  const canDeleteFields = isSuperAdmin

  useEffect(() => {
    if (canManageFields) {
      fetchCustomFields()
    }
  }, [canManageFields])

  const fetchCustomFields = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(getApiUrl('custom-fields'), createFetchConfig())

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: '獲取自訂欄位失敗' }))
        throw new Error(errorData.message || '獲取自訂欄位失敗')
      }

      const data = await response.json()

      // 確保資料格式正確
      if (data.success && data.data && Array.isArray(data.data.customFields)) {
        setCustomFields(data.data.customFields)
      } else {
        console.warn('Custom fields API returned unexpected format:', data)
        setCustomFields([])
      }
    } catch (error: any) {
      console.error('Error fetching custom fields:', error)
      setError(error.message || '獲取自訂欄位失敗')
    } finally {
      setLoading(false)
    }
  }

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text })
    setTimeout(() => setMessage(null), 5000)
  }

  const handleCreateField = async () => {
    if (!newField.name.trim() || !newField.key.trim()) {
      showMessage('error', '請填寫欄位名稱和鍵值')
      return
    }

    // 驗證鍵值格式
    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(newField.key)) {
      showMessage('error', '鍵值必須以英文字母開頭，只能包含英文字母、數字和底線')
      return
    }

    // 驗證 SELECT 類型必須有選項
    if (newField.type === 'SELECT' && newField.options.filter(opt => opt.trim()).length === 0) {
      showMessage('error', '下拉選單類型必須提供至少一個選項')
      return
    }

    try {
      setLoading(true) // 設定載入狀態

      const requestData = {
        ...newField,
        ...(newField.type === 'SELECT' && { options: newField.options.filter(opt => opt.trim()) })
      }

      const response = await fetch(getApiUrl('custom-fields'), createFetchConfig({
        method: 'POST',
        body: JSON.stringify(requestData)
      }))

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || '建立自訂欄位失敗')
      }

      const result = await response.json()

      // 立即將新建立的欄位加入到現有列表中，確保即時顯示
      if (result.success && result.data?.customField) {
        setCustomFields(prevFields => [result.data.customField, ...prevFields])
      }

      showMessage('success', '自訂欄位建立成功')
      setShowCreateForm(false)
      setNewField({
        name: '',
        key: '',
        type: 'TEXT',
        options: [''],
        required: false,
        isGlobal: true
      })

      // 重新載入完整資料以確保一致性
      await fetchCustomFields()
    } catch (error: any) {
      showMessage('error', error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateField = async (fieldId: string, field: string, value: any) => {
    try {
      const updateData: any = {}

      if (field === 'options' && Array.isArray(value)) {
        updateData.options = value.filter((opt: string) => opt.trim())
        if (updateData.options.length === 0) {
          showMessage('error', '下拉選單必須至少有一個選項')
          return
        }
      } else {
        updateData[field] = value
      }

      const response = await fetch(getApiUrl(`custom-fields/${fieldId}`), createFetchConfig({
        method: 'PUT',
        body: JSON.stringify(updateData)
      }))

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || '更新自訂欄位失敗')
      }

      const result = await response.json()

      // 立即更新本地狀態
      if (result.success && result.data?.customField) {
        setCustomFields(prevFields =>
          prevFields.map(f => f.id === fieldId ? result.data.customField : f)
        )
      }

      showMessage('success', '自訂欄位更新成功')
      setEditingField(null)
      setEditingType(null)

      // 重新載入完整資料以確保一致性
      await fetchCustomFields()
    } catch (error: any) {
      showMessage('error', error.message)
    }
  }

  const handleDeleteField = async (fieldId: string, fieldName: string) => {
    if (!confirm(`確定要刪除自訂欄位「${fieldName}」嗎？此操作無法復原。`)) {
      return
    }

    try {
      const response = await fetch(getApiUrl(`custom-fields/${fieldId}`), createFetchConfig({
        method: 'DELETE'
      }))

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || '刪除自訂欄位失敗')
      }

      // 立即從本地狀態中移除該欄位
      setCustomFields(prevFields => prevFields.filter(f => f.id !== fieldId))

      showMessage('success', '自訂欄位刪除成功')

      // 重新載入完整資料以確保一致性
      await fetchCustomFields()
    } catch (error: any) {
      showMessage('error', error.message)
    }
  }

  const startEdit = (fieldId: string, type: 'name' | 'options' | 'required', currentValue: any) => {
    setEditingField(fieldId)
    setEditingType(type)
    setEditingValue(currentValue)
  }

  const cancelEdit = () => {
    setEditingField(null)
    setEditingType(null)
    setEditingValue('')
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'TEXT':
        return <DocumentTextIcon className="w-4 h-4" />
      case 'SELECT':
        return <ListBulletIcon className="w-4 h-4" />
      case 'NUMBER':
        return <HashtagIcon className="w-4 h-4" />
      case 'DATE':
        return <CalendarIcon className="w-4 h-4" />
      default:
        return <AdjustmentsHorizontalIcon className="w-4 h-4" />
    }
  }

  const getTypeDisplayName = (type: string) => {
    switch (type) {
      case 'TEXT': return '文字'
      case 'SELECT': return '下拉選單'
      case 'NUMBER': return '數字'
      case 'DATE': return '日期'
      default: return type
    }
  }

  if (!canManageFields) {
    return (
      <div className="text-center py-12">
        <InformationCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">存取被拒</h3>
        <p className="mt-1 text-sm text-gray-500">您沒有管理自訂欄位的權限。</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-sm text-gray-500">載入中...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <InformationCircleIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">載入失敗</h3>
        <p className="mt-1 text-sm text-gray-500">{error}</p>
        <button
          onClick={fetchCustomFields}
          className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          重新載入
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">自訂欄位管理</h2>
          <p className="mt-1 text-sm text-gray-600">管理系統自訂欄位，可用於各專案的議題</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 flex items-center space-x-2"
        >
          <PlusIcon className="w-4 h-4" />
          <span>新增欄位</span>
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-md ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          <div className="flex">
            <div className="flex-shrink-0">
              {message.type === 'success' ? (
                <CheckIcon className="h-5 w-5 text-green-400" />
              ) : (
                <XMarkIcon className="h-5 w-5 text-red-400" />
              )}
            </div>
            <div className="ml-3">
              <p className="text-sm">{message.text}</p>
            </div>
          </div>
        </div>
      )}

      {/* Create Form */}
      {showCreateForm && (
        <div className="bg-white p-6 border border-gray-200 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">建立新的自訂欄位</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                欄位名稱 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newField.name}
                onChange={(e) => setNewField(prev => ({ ...prev, name: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="例如：優先級、客戶名稱"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                欄位鍵值 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newField.key}
                onChange={(e) => setNewField(prev => ({ ...prev, key: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="例如：priority, customer_name"
              />
              <p className="mt-1 text-xs text-gray-500">只能包含英文字母、數字和底線，需以字母開頭</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">欄位類型</label>
              <select
                value={newField.type}
                onChange={(e) => setNewField(prev => ({ ...prev, type: e.target.value as any }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="TEXT">文字</option>
                <option value="SELECT">下拉選單</option>
                <option value="NUMBER">數字</option>
                <option value="DATE">日期</option>
              </select>
            </div>

            <div className="flex items-center space-x-6">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={newField.required}
                  onChange={(e) => setNewField(prev => ({ ...prev, required: e.target.checked }))}
                  className="mr-2"
                />
                必填欄位
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={newField.isGlobal}
                  onChange={(e) => setNewField(prev => ({ ...prev, isGlobal: e.target.checked }))}
                  className="mr-2"
                />
                全域欄位
              </label>
            </div>

            {newField.type === 'SELECT' && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">選項設定</label>
                {newField.options.map((option, index) => (
                  <div key={index} className="flex items-center space-x-2 mb-2">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => {
                        const newOptions = [...newField.options]
                        newOptions[index] = e.target.value
                        setNewField(prev => ({ ...prev, options: newOptions }))
                      }}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={`選項 ${index + 1}`}
                    />
                    <button
                      onClick={() => {
                        const newOptions = newField.options.filter((_, i) => i !== index)
                        setNewField(prev => ({ ...prev, options: newOptions }))
                      }}
                      className="text-red-500 hover:text-red-700"
                      disabled={newField.options.length <= 1}
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => setNewField(prev => ({ ...prev, options: [...prev.options, ''] }))}
                  className="text-blue-500 hover:text-blue-700 text-sm"
                >
                  + 新增選項
                </button>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => setShowCreateForm(false)}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              取消
            </button>
            <button
              onClick={handleCreateField}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
            >
              建立欄位
            </button>
          </div>
        </div>
      )}

      {/* Custom Fields List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            現有自訂欄位 ({customFields.length})
          </h3>
        </div>

        {customFields.length === 0 ? (
          <div className="text-center py-12 border-t border-gray-200">
            <AdjustmentsHorizontalIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">尚無自訂欄位</h3>
            <p className="mt-1 text-sm text-gray-500">建立第一個自訂欄位來開始使用。</p>
          </div>
        ) : (
          <div className="border-t border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      欄位資訊
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      類型
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      選項
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      狀態
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      使用統計
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      建立者
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {customFields.map((field) => (
                    <tr key={field.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          {editingField === field.id && editingType === 'name' ? (
                            <div className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                className="text-sm border border-gray-300 rounded px-2 py-1"
                                autoFocus
                              />
                              <button
                                onClick={() => handleUpdateField(field.id, 'name', editingValue)}
                                className="text-green-600 hover:text-green-900"
                              >
                                <CheckIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={cancelEdit}
                                className="text-red-600 hover:text-red-900"
                              >
                                <XMarkIcon className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <div>
                                <div className="text-sm font-medium text-gray-900">{field.name}</div>
                                <div className="text-sm text-gray-500 font-mono">{field.key}</div>
                              </div>
                              <button
                                onClick={() => startEdit(field.id, 'name', field.name)}
                                className="text-gray-400 hover:text-blue-600"
                                title="編輯名稱"
                              >
                                <PencilIcon className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-1">
                          {getTypeIcon(field.type)}
                          <span className="text-sm text-gray-900">{getTypeDisplayName(field.type)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {field.type === 'SELECT' ? (
                          editingField === field.id && editingType === 'options' ? (
                            <div className="space-y-2">
                              {editingValue.map((option: string, index: number) => (
                                <div key={index} className="flex items-center space-x-2">
                                  <input
                                    type="text"
                                    value={option}
                                    onChange={(e) => {
                                      const newOptions = [...editingValue]
                                      newOptions[index] = e.target.value
                                      setEditingValue(newOptions)
                                    }}
                                    className="text-sm border border-gray-300 rounded px-2 py-1 flex-1"
                                  />
                                  <button
                                    onClick={() => {
                                      const newOptions = editingValue.filter((_: any, i: number) => i !== index)
                                      setEditingValue(newOptions)
                                    }}
                                    className="text-red-500 hover:text-red-700"
                                    disabled={editingValue.length <= 1}
                                  >
                                    <XMarkIcon className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                              <div className="flex items-center space-x-2 mt-2">
                                <button
                                  onClick={() => setEditingValue([...editingValue, ''])}
                                  className="text-blue-500 hover:text-blue-700 text-xs"
                                >
                                  + 新增選項
                                </button>
                                <button
                                  onClick={() => handleUpdateField(field.id, 'options', editingValue)}
                                  className="text-green-600 hover:text-green-900"
                                >
                                  <CheckIcon className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  <XMarkIcon className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-start space-x-2">
                              <div className="text-sm text-gray-900 max-w-xs">
                                {field.options ? (
                                  <div className="space-y-1">
                                    {field.options.slice(0, 3).map((option: string, index: number) => (
                                      <div key={index} className="bg-gray-100 px-2 py-1 rounded text-xs">
                                        {option}
                                      </div>
                                    ))}
                                    {field.options.length > 3 && (
                                      <div className="text-xs text-gray-500">
                                        +{field.options.length - 3} 個選項...
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-gray-400">無選項</span>
                                )}
                              </div>
                              {field.type === 'SELECT' && (
                                <button
                                  onClick={() => startEdit(field.id, 'options', field.options || [])}
                                  className="text-gray-400 hover:text-blue-600"
                                  title="編輯選項"
                                >
                                  <PencilIcon className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          )
                        ) : (
                          <span className="text-gray-400 text-sm">不適用</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2">
                            {editingField === field.id && editingType === 'required' ? (
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={editingValue}
                                  onChange={(e) => setEditingValue(e.target.checked)}
                                  className="mr-1"
                                />
                                <button
                                  onClick={() => handleUpdateField(field.id, 'required', editingValue)}
                                  className="text-green-600 hover:text-green-900"
                                >
                                  <CheckIcon className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  <XMarkIcon className="w-4 h-4" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-2">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  field.required
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {field.required ? '必填' : '選填'}
                                </span>
                                <button
                                  onClick={() => startEdit(field.id, 'required', field.required)}
                                  className="text-gray-400 hover:text-blue-600"
                                  title="切換必填狀態"
                                >
                                  <PencilIcon className="w-3 h-3" />
                                </button>
                              </div>
                            )}
                          </div>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            field.isGlobal
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {field.isGlobal ? '全域' : '專案特定'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 space-y-1">
                          <div className="flex items-center space-x-1">
                            <ChartBarIcon className="w-4 h-4 text-gray-400" />
                            <span>{field._count.projects} 個專案</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <DocumentTextIcon className="w-4 h-4 text-gray-400" />
                            <span>{field._count.values} 次使用</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div>
                          <div className="font-medium">{field.creator.name}</div>
                          <div className="text-xs">{field.creator.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {canDeleteFields && field._count.values === 0 && (
                          <button
                            onClick={() => handleDeleteField(field.id, field.name)}
                            className="text-red-600 hover:text-red-900 ml-2"
                            title="刪除欄位"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CustomFieldManagement